import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUserData } from '../../context/UserDataContext';
import { useAccountData } from '../../context/AccountDataContext';
import { Link, Navigate } from 'react-router-dom';
import * as userService from '../../services/userService';
import * as paymentService from '../../services/paymentService';
import * as connectionFeeService from '../../services/connectionFeeService';
import * as levyCollectionService from '../../services/levyCollectionService';
import { 
  UsageStatsCard, 
  PrepaidAccountCard, 
  UnitCard,
  RecentPrepaidTransactionsCard,
  ConnectionFeeCard,
  LevyCollectionCard
} from '../../components/cards';

function PrepaidUserDashboard() {
  const { user, account, unit: authUnit, hasRole, loading: authLoading } = useAuth();
  const { userData, loading: userDataLoading } = useUserData();
  const { 
    accountBalance, 
    usageData, 
    billing, 
    loading: accountDataLoading, 
    isPrepaidUser,
    lastUpdated,
    resetFailedAttempts,
    forceRefresh
  } = useAccountData();
  
  const [loading, setLoading] = useState(true);
  const [debugMode, setDebugMode] = useState(false); // For debugging user information
  const [unit, setUnit] = useState(null); // Local unit state to store the detailed unit info
  const [stats, setStats] = useState({
    balance: 0,
    usage: {
      today: 0,
      thisWeek: 0,
      thisMonth: 0,
    },
    transactions: [],
  });
  const [hasInitialized, setHasInitialized] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null); // Track the last refresh time
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [unitLoading, setUnitLoading] = useState(true);
  const [unitError, setUnitError] = useState(false);
  const [connectionFeeEnabled, setConnectionFeeEnabled] = useState(false);
  const [connectionFeeData, setConnectionFeeData] = useState(null);
  const [connectionFeeLoading, setConnectionFeeLoading] = useState(false);
  const [levyCollection, setLevyCollection] = useState(null);
  const [levyAllocations, setLevyAllocations] = useState([]);
  const [levyCollectionLoading, setLevyCollectionLoading] = useState(false);

  const levyUnitUuid = unit?.uuid
    || unit?.unitUuid
    || user?.unitUuid
    || account?.unitUuid
    || authUnit?.uuid
    || authUnit?.unitUuid;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Toggle debug mode
  const toggleDebugMode = () => {
    setDebugMode(prev => !prev);
  };

  // Initialize data when component mounts
  useEffect(() => {
    // Only run this effect once when component mounts
    if (!hasInitialized && user && userData?.accountUuid) {
      // console.log('Initializing PrepaidUserDashboard data');
      
      // Reset any existing failed attempts 
      resetFailedAttempts();
      
      setHasInitialized(true);
    }
  }, [hasInitialized, user, userData?.accountUuid, resetFailedAttempts]);

  // Manually refresh data - used for the refresh button
  const handleRefreshData = useCallback(() => {
    // Check if we're already loading
    if (loading || accountDataLoading) {
      // console.log('Already loading, skipping manual refresh');
      return;
    }
    
    // Limit refresh attempts to once every 5 seconds
    if (lastRefreshTime && Date.now() - lastRefreshTime < 5000) {
      // console.log('Refresh attempted too frequently. Please wait a few seconds.');
      return;
    }
    
    // console.log("Manual refresh triggered");
    
    // Log current localStorage state
    // console.log('Current userData in localStorage:', localStorage.getItem('userData'));
    
    // Make sure site and unit info are available in userData
    const userDataObj = userData || JSON.parse(localStorage.getItem('userData') || '{}');
    
    if (!userDataObj.siteName || !userDataObj.unitId) {
      // console.error('Cannot refresh data: missing siteName or unitId in userData');
      return;
    }
    
    // console.log(`Refreshing with site=${userDataObj.siteName}, unit=${userDataObj.unitId}`);
    
    // Reset failures first
    resetFailedAttempts();
    
    // Update last refresh time
    setLastRefreshTime(Date.now());
    
    // Then force refresh
    forceRefresh().then(() => {
      // console.log("Manual refresh completed");
    }).catch(err => {
      // console.error("Manual refresh failed:", err);
    });

    // Add payment refresh with cache clearing
    if (userData?.accountUuid) {
      // Clear the transaction cache
      paymentService.clearTransactionCache(userData.accountUuid);
      // console.log('Cleared transaction cache before refresh');
      
      paymentService.resetFailedRequests();
      setPaymentsLoading(true);
      
      paymentService.getPaymentsByAccount(userData.accountUuid)
        .then(paymentData => {
          if (paymentData && Array.isArray(paymentData)) {
            setPayments(paymentData);
            // console.log('Payment data refreshed:', paymentData.length, 'transactions');
            
            if (paymentData.length > 0) {
              try {
                const cacheData = {
                  data: paymentData,
                  timestamp: Date.now()
                };
                sessionStorage.setItem(`prepaid_transactions_${userData.accountUuid}`, JSON.stringify(cacheData));
              } catch (e) {
                // console.error('Error updating transaction cache:', e);
              }
            }
          }
        })
        .catch(err => {
          // console.error('Failed to refresh payment data:', err);
        })
        .finally(() => {
          setPaymentsLoading(false);
        });
    }
  }, [loading, accountDataLoading, resetFailedAttempts, forceRefresh, userData, lastRefreshTime]);

  // Check if user has any staff role (ADMIN, MANAGEMENT, OPERATOR)
  const isStaff = user && (hasRole('ADMIN') || hasRole('MANAGEMENT') || hasRole('OPERATOR'));

  // Redirect staff users directly to admin dashboard
  if (user && isStaff && !authLoading) {
    return <Navigate to="/admin" replace />;
  }

  // Fetch unit information using new endpoint
  useEffect(() => {
    let isMounted = true;
    
    const fetchUnitInfo = async () => {
      if (!unit && user && user.siteUuid && user.unitId && isMounted) {
        try {
          setUnitLoading(true);
          setUnitError(false);
          // console.log(`Fetching unit info for site: ${user.siteUuid}, unit: ${user.unitId}`);
          const unitInfo = await userService.getUnitByUnitId(user.siteUuid, user.unitId);
          
          if (unitInfo && isMounted) {
            setUnit(unitInfo);
            // console.log('Unit information fetched:', unitInfo);
          } else if (isMounted) {
            // No unit info returned
            setUnitError(true);
          }
        } catch (error) {
          // console.error('Failed to fetch unit information:', error);
          if (isMounted) {
            setUnitError(true);
          }
          
          // Fallback to the unit info from auth context if available
          if (authUnit && isMounted) {
            setUnit(authUnit);
          }
        } finally {
          if (isMounted) {
            setUnitLoading(false);
          }
        }
      } else if (isMounted) {
        // If we already have unit data or missing required data
        setUnitLoading(false);
      }
    };

    fetchUnitInfo();
    
    return () => {
      isMounted = false;
    };
  }, [user?.siteUuid, user?.unitId, authUnit, unit]);

  // Update stats based on AccountDataContext
  useEffect(() => {
    // Skip if still loading
    if (accountDataLoading) {
      return;
    }

    // Update balance data
    let balance = 0;
    
    // Check multiple sources for balance in priority order
    if (accountBalance) {
      if (typeof accountBalance.balance === 'number') {
        balance = accountBalance.balance;
      } else if (typeof accountBalance.remainingCredit === 'number') {
        balance = accountBalance.remainingCredit;
      }
    }
    
    // If balance is still 0, try to get it from userData.account
    if (balance === 0 && userData?.account?.balance) {
      balance = userData.account.balance;
      // console.log('Using balance from userData.account:', balance);
    }
    
    // If balance is still 0, try to get it from the auth account object
    if (balance === 0 && account?.balance) {
      balance = account.balance;
      // console.log('Using balance from auth account:', balance);
    }

    setStats(prevStats => ({
      ...prevStats,
      balance
    }));

    // Update usage data if available (which it currently won't be)
    // Default to safe values since the API doesn't provide this data
    setStats(prevStats => ({
      ...prevStats,
      usage: {
        today: usageData?.readings && usageData.readings.length > 0 
          ? usageData.readings[usageData.readings.length - 1].kwh 
          : 0,
        thisWeek: usageData?.readings 
          ? usageData.readings.reduce((sum, reading) => sum + reading.kwh, 0)
          : 0,
        thisMonth: usageData?.summary?.monthly?.currentMonth || 0
      }
    }));

    // Update transaction data if available
    if (accountBalance && accountBalance.transactions) {
      setStats(prevStats => ({
        ...prevStats,
        transactions: accountBalance.transactions
      }));
    }

    // Once we've updated stats, mark dashboard as not loading
    setLoading(false);
  }, [accountBalance, usageData, billing, accountDataLoading, userData, account]);

  // Fetch payment data
  useEffect(() => {
    const fetchPaymentData = async () => {
      if (user && userData?.accountUuid) {
        try {
          setPaymentsLoading(true);
          
          // Check for stale cache flag first
          const isStale = sessionStorage.getItem('transaction_cache_stale') === 'true';
          if (isStale) {
            // console.log('Transaction cache marked as stale, fetching fresh data');
            sessionStorage.removeItem('transaction_cache_stale');
            // Continue with fresh API request
          } else {
            // Check cache first
            const cacheKey = `prepaid_transactions_${userData.accountUuid}`;
            const cachedData = sessionStorage.getItem(cacheKey);
            
            if (cachedData) {
              try {
                const parsedCache = JSON.parse(cachedData);
                const tenMinutesInMs = 10 * 60 * 1000;
                
                if (Date.now() - parsedCache.timestamp < tenMinutesInMs
                    && Array.isArray(parsedCache.data) && parsedCache.data.length > 0) {
                  // console.log('Using cached transaction data');
                  setPayments(parsedCache.data);
                  setPaymentsLoading(false);
                  return;
                } else {
                  // console.log('Cached transaction data expired or empty, fetching fresh');
                }
              } catch (e) {
                // console.error('Error parsing cached transaction data:', e);
              }
            }
          }
          
          // Fetch from API if no valid cache found
          // console.log('Fetching fresh payment data for account:', userData.accountUuid);
          const paymentData = await paymentService.getPaymentsByAccount(userData.accountUuid);
          
          if (paymentData && Array.isArray(paymentData)) {
            setPayments(paymentData);
            // console.log('Payment data fetched:', paymentData.length, 'transactions');
            
            // Only cache non-empty results to avoid locking out future fetches
            if (paymentData.length > 0) {
              try {
                const cacheData = {
                  data: paymentData,
                  timestamp: Date.now()
                };
                sessionStorage.setItem(`prepaid_transactions_${userData.accountUuid}`, JSON.stringify(cacheData));
              } catch (e) {
                // console.error('Error saving transaction data to cache:', e);
              }
            }
          }
        } catch (error) {
          // console.error('Failed to fetch payment data:', error);
        } finally {
          setPaymentsLoading(false);
        }
      }
    };

    if (hasInitialized) {
      fetchPaymentData();
    }
  }, [user, userData?.accountUuid, hasInitialized]);

  // Fetch connection fee data if the site supports it
  useEffect(() => {
    if (!hasInitialized || !user?.siteUuid || !userData?.accountUuid) return;

    let cancelled = false;

    const fetchConnectionFee = async () => {
      setConnectionFeeLoading(true);
      try {
        const summary = await connectionFeeService.getConnectionFeeSummary(user.siteUuid);
        if (cancelled) return;

        if (summary) {
          setConnectionFeeEnabled(true);
          const chargesData = await connectionFeeService.getConnectionFeeCharges(userData.accountUuid);
          if (!cancelled) {
            setConnectionFeeData(chargesData);
          }
        } else {
          setConnectionFeeEnabled(false);
          setConnectionFeeData(null);
        }
      } catch (err) {
        // console.error('Error loading connection fee data:', err);
        if (!cancelled) {
          setConnectionFeeEnabled(false);
          setConnectionFeeData(null);
        }
      } finally {
        if (!cancelled) setConnectionFeeLoading(false);
      }
    };

    fetchConnectionFee();
    return () => { cancelled = true; };
  }, [hasInitialized, user?.siteUuid, userData?.accountUuid]);

  // Fetch active levy collection data for the prepaid unit, if configured.
  useEffect(() => {
    if (!hasInitialized || !levyUnitUuid) return;

    let cancelled = false;

    const fetchLevyCollection = async () => {
      setLevyCollectionLoading(true);
      try {
        const currentLevy = await levyCollectionService.getCurrentLevyCollection(levyUnitUuid);
        if (cancelled) return;

        if (currentLevy?.status === 'ACTIVE') {
          setLevyCollection(currentLevy);
          const allocations = await levyCollectionService.getLevyAllocationsForUnit(levyUnitUuid);
          if (!cancelled) {
            setLevyAllocations(allocations);
          }
        } else {
          setLevyCollection(null);
          setLevyAllocations([]);
        }
      } catch (err) {
        // console.error('Error loading levy collection data:', err);
        if (!cancelled) {
          setLevyCollection(null);
          setLevyAllocations([]);
        }
      } finally {
        if (!cancelled) setLevyCollectionLoading(false);
      }
    };

    fetchLevyCollection();
    return () => { cancelled = true; };
  }, [hasInitialized, levyUnitUuid]);

  // Show loading indicator if auth is still loading
  if (authLoading || userDataLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <div className="ml-4 text-lg font-medium">Loading user data...</div>
      </div>
    );
  }

  // Show login prompt if user is not authenticated
  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-2xl font-bold mb-4">Please log in to view your dashboard</h2>
        <div className="flex space-x-4">
          <Link to="/login" className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded">
            Sign In
          </Link>
          <Link to="/register" className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded">
            Create Account
          </Link>
        </div>
      </div>
    );
  }

  // Show loading indicator for dashboard data
  if (loading || accountDataLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <div className="ml-4 text-lg font-medium">Loading dashboard data...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-hooray-red font-mont">Welcome, {userData?.firstName || user.firstName || user.name || 'User'}!</h1>
        <p className="text-gray-600 text-hooray-red">Here's an overview of your Hooray Power account.</p>
        
        {/* Debug and Refresh buttons - Temporarily commented out
        <div className="flex gap-2 mt-4">
          <button 
            onClick={toggleDebugMode}
            className={`px-4 py-2 rounded ${debugMode ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-500 hover:bg-gray-600'} text-white`}
          >
            {debugMode ? 'Hide Debug' : 'Debug'}
          </button>
          
          <button 
            onClick={handleRefreshData}
            className={`px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 text-white ${accountDataLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={accountDataLoading}
          >
            {accountDataLoading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
        */}
        
        {/* {lastUpdated && (
          <p className="text-xs text-gray-500 mt-2">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </p>
        )} */}
      </div>
      
      {/* Debug section - Temporarily commented out
      {debugMode && (
        <div className="mb-8 border-2 border-gray-300 p-6 rounded-lg">
          <h2 className="text-2xl font-bold mb-4">Debug: User Account Information</h2>
          
          <div className="bg-gray-100 p-4 rounded-lg">
            <h3 className="text-lg font-bold mb-2">User Information:</h3>
            <div className="bg-white p-4 rounded overflow-auto">
              <p><strong>Account UUID:</strong> {user?.uuid || 'Not available'}</p>
              <p><strong>User ID:</strong> {user?.id || 'Not available'}</p>
              <p><strong>Email:</strong> {user?.email || 'Not available'}</p>
              <p><strong>Name:</strong> {user?.firstName} {user?.lastName} {user?.name || ''}</p>
              <p><strong>Roles:</strong> {user?.roles?.join(', ') || 'None'}</p>
              <p><strong>Site Name:</strong> {user?.siteName || 'Not available'}</p>
              <p><strong>Site UUID:</strong> {user?.siteUuid || 'Not available'}</p>
              <p><strong>Unit ID:</strong> {user?.unitId !== undefined ? user.unitId : 'Not available'}</p>
              <p><strong>Full User Object:</strong></p>
              <pre className="text-xs mt-2 bg-gray-100 p-2 rounded overflow-x-auto">
                {JSON.stringify(user, null, 2)}
              </pre>
            </div>
          </div>
          
          {userData && (
            <div className="bg-gray-100 p-4 rounded-lg mt-4">
              <h3 className="text-lg font-bold mb-2">UserData Context:</h3>
              <div className="bg-white p-4 rounded overflow-auto">
                <p><strong>Account UUID:</strong> {userData?.uuid || 'Not available'}</p>
                <p><strong>User ID:</strong> {userData?.id || 'Not available'}</p>
                <p><strong>Email:</strong> {userData?.email || 'Not available'}</p>
                <p><strong>Name:</strong> {userData?.firstName} {userData?.lastName}</p>
                <p><strong>Full UserData Object:</strong></p>
                <pre className="text-xs mt-2 bg-gray-100 p-2 rounded overflow-x-auto">
                  {JSON.stringify(userData, null, 2)}
                </pre>
              </div>
            </div>
          )}
          
          <div className="bg-gray-100 p-4 rounded-lg mt-4">
            <h3 className="text-lg font-bold mb-2">Billing API Data:</h3>
            <div className="bg-white p-4 rounded overflow-auto">
              {account ? (
                <>
                  <p><strong>Account UUID:</strong> {account?.uuid || 'Not available'}</p>
                  <p><strong>Balance:</strong> {account?.balance !== undefined ? account.balance : 'Not available'}</p>
                  <p><strong>Full Account Object:</strong></p>
                  <pre className="text-xs mt-2 bg-gray-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify(account, null, 2)}
                  </pre>
                </>
              ) : (
                <p className="text-red-500 font-medium">No account data available. This likely means the API call to get account data failed or returned no data.</p>
              )}
            </div>
          </div>
          
          <div className="bg-gray-100 p-4 rounded-lg mt-4">
            <h3 className="text-lg font-bold mb-2">Unit Information:</h3>
            <div className="bg-white p-4 rounded overflow-auto">
              {unit ? (
                <>
                  <p><strong>Unit Number:</strong> {unit?.unitNumber || 'Not available'}</p>
                  <p><strong>Site Name:</strong> {unit?.siteName || 'Not available'}</p>
                  <p><strong>Full Unit Object:</strong></p>
                  <pre className="text-xs mt-2 bg-gray-100 p-2 rounded overflow-x-auto">
                    {JSON.stringify(unit, null, 2)}
                  </pre>
                </>
              ) : (
                <p className="text-red-500 font-medium">No unit data available. This likely means the unit information is not set.</p>
              )}
            </div>
          </div>

          <div className="bg-gray-100 p-4 rounded-lg mt-4">
            <h3 className="text-lg font-bold mb-2">Debug Information:</h3>
            <div className="bg-white p-4 rounded overflow-auto">
              <p><strong>Account Data Type:</strong> {typeof account}</p>
              <p><strong>Unit Data Type:</strong> {typeof unit}</p>
              <p><strong>Has siteName in user profile:</strong> {user?.siteName ? 'Yes' : 'No'}</p>
              <p><strong>Has siteUuid in user profile:</strong> {user?.siteUuid ? 'Yes' : 'No'}</p>
              <p><strong>Has unitId in user profile:</strong> {user?.unitId !== undefined ? 'Yes' : 'No'}</p>
              <p><strong>unitId value:</strong> {user?.unitId}</p>
              <p><strong>Stats Balance:</strong> {stats.balance}</p>
              <p><strong>Is Prepaid User:</strong> {isPrepaidUser ? isPrepaidUser() ? 'Yes' : 'No' : 'Function not available'}</p>
            </div>
          </div>
        </div>
      )}
      */}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Account Balance Card - Using PrepaidAccountCard component */}
        <PrepaidAccountCard 
          balance={stats.balance}
          showBuyButton={true}
          buttonText="Buy Electricity"
          siteName={unit?.siteName}
        />

        {/* Usage Card - Using UsageStatsCard component */} 
        {/* <UsageStatsCard 
          usage={stats.usage}
          showViewDataLink={true}
        /> */}

        {/* Unit Information Card - Using UnitCard component */}
        <UnitCard 
          unit={unit} 
          isPrepaid={true} 
          isLoading={unitLoading}
          hasError={unitError}
          hasActiveLevyCollection={levyCollection?.status === 'ACTIVE'}
        />
      </div>

      <LevyCollectionCard
        levy={levyCollection}
        allocations={levyAllocations}
        loading={levyCollectionLoading}
        onRefresh={async () => {
          if (!levyUnitUuid) return;

          setLevyCollectionLoading(true);
          try {
            const currentLevy = await levyCollectionService.getCurrentLevyCollection(levyUnitUuid);
            setLevyCollection(currentLevy?.status === 'ACTIVE' ? currentLevy : null);
            setLevyAllocations(
              currentLevy?.status === 'ACTIVE'
                ? await levyCollectionService.getLevyAllocationsForUnit(levyUnitUuid)
                : []
            );
          } finally {
            setLevyCollectionLoading(false);
          }
        }}
      />

      {/* Recent Transactions Card */}
      {paymentsLoading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          <div className="ml-4 text-sm font-medium">Loading payment data...</div>
        </div>
      ) : (
        <RecentPrepaidTransactionsCard 
          accountUuid={userData?.accountUuid}
          transactions={payments.length > 0 ? payments : []}
          useCache={true}
          siteName={unit?.siteName}
        />
      )}

      {/* Connection Fee Charges (only shown when the site has a connection fee) */}
      {connectionFeeEnabled && (
        <ConnectionFeeCard
          data={connectionFeeData}
          loading={connectionFeeLoading}
          onRefresh={async () => {
            setConnectionFeeLoading(true);
            try {
              const data = await connectionFeeService.getConnectionFeeCharges(userData?.accountUuid);
              setConnectionFeeData(data);
            } finally {
              setConnectionFeeLoading(false);
            }
          }}
        />
      )}
    </div>
  );
}

export default PrepaidUserDashboard; 